import type { Variants } from "framer-motion";

// Transition variants for different page types
export const pageTransitions = {
  // Netflix-style cinematic fade with blur for Splash → SignIn
  cinematicFade: {
    initial: {
      opacity: 0,
      filter: "blur(8px)",
      scale: 1,
    },
    animate: {
      opacity: 1,
      filter: "blur(0px)",
      scale: 1,
      transition: {
        duration: 0.1,
        ease: [0.42, 0, 1, 1],
      },
    },
    exit: {
      opacity: 0,
      filter: "blur(8px)",
      scale: 1,
      transition: {
        duration: 1.2,
        ease: [0, 0, 0.58, 1], // Custom easing curve
      },
    },
  } as Variants,

  // Smooth scale-down entrance for SignIn → Home
  scaleDown: {
    initial: {
      opacity: 0.5,
      scale: 1.02,
      filter: "blur(2px)",
    },
    animate: {
      opacity: 1,
      scale: 1,
      filter: "blur(0px)",
      transition: {
        duration: 0.8,
        ease: [0, 0, 0.2, 1],
      },
    },
    exit: {
      opacity: 0,
      scale: 1.05,
      filter: "blur(2px)",
      transition: {
        duration: 0.2,
        ease: [0.4, 0, 1, 1],
      },
    },
  } as Variants,

  // Subtle slide transitions for navigation within app
  slideHorizontal: {
    initial: {
      opacity: 0,
      x: 20,
      filter: "blur(1px)",
    },
    animate: {
      opacity: 1,
      x: 0,
      filter: "blur(0px)",
      transition: {
        duration: 0.5,
        ease: [0, 0, 0.2, 1],
      },
    },
    exit: {
      opacity: 0,
      x: -20,
      filter: "blur(1px)",
      transition: {
        duration: 0.3,
        ease: [0.4, 0, 1, 1],
      },
    },
  } as Variants,

  // Full-screen fade transitions for player entry/exit
  fullScreenFade: {
    initial: {
      opacity: 0,
      filter: "blur(0px)",
      scale: 1,
    },
    animate: {
      opacity: 1,
      filter: "blur(0px)",
      scale: 1,
      transition: {
        duration: 0.1,
        ease: [0.42, 0, 1, 1],
      },
    },
    exit: {
      opacity: 0,
      filter: "blur(0px)",
      scale: 1,
      transition: {
        duration: 0.01,
        ease: [0, 0, 0.58, 1], // Custom easing curve
      },
    },
  } as Variants,

  // Quick fade for general page transitions
  fade: {
    initial: {
      opacity: 0,
    },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: "easeIn",
      },
    },
  } as Variants,
};

// Transition configs for different contexts
export const transitionConfig = {
  splash: {
    duration: 1.2,
    ease: [0.4, 0, 0.2, 1],
  },
  auth: {
    duration: 0.8,
    ease: [0, 0, 0.2, 1],
  },
  navigation: {
    duration: 0.5,
    ease: [0, 0, 0.2, 1],
  },
  player: {
    duration: 0.6,
    ease: "easeInOut",
  },
};

/**
 * Extract the exit transition duration from a transition variant
 * @param variant - The Framer Motion variant object
 * @returns Duration in milliseconds (defaults to 300ms if not found)
 */
export function getExitDuration(variant: Variants): number {
  const exit = variant.exit;
  if (typeof exit === "object" && exit && "transition" in exit) {
    const transition = exit.transition;
    if (typeof transition === "object" && transition && "duration" in transition) {
      const duration = transition.duration;
      if (typeof duration === "number") {
        return duration * 1000; // Convert seconds to milliseconds
      }
    }
  }
  return 300; // Default fallback
}
